import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import Header from "../components/header";
import DefaultFooter from "../components/default-footer";
import WhatsAppButton from "../components/WhatsAppButton";
import { API_URL } from "../config";

type AccountType = "individual" | "company";

interface FormState {
  type: AccountType;
  company_name: string;
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
  address: string;
  country: string;
  password: string;
  confirmPassword: string;
}

const INITIAL_FORM: FormState = {
  type: "individual",
  company_name: "",
  firstname: "",
  lastname: "",
  email: "",
  phone: "",
  address: "",
  country: "Oman",
  password: "",
  confirmPassword: "",
};

// At least 8 characters, one uppercase, one lowercase, one digit
const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
const EMAIL_RULE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Accepts Oman numbers (+968 XXXXXXXX) or general international format
const PHONE_RULE = /^\+?\d{7,15}$/;

const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia",
  "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados",
  "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina",
  "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cambodia",
  "Cameroon", "Canada", "Cape Verde", "Central African Republic", "Chad", "Chile",
  "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus",
  "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador",
  "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini",
  "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany",
  "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
  "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq",
  "Ireland", "Israel", "Italy", "Ivory Coast", "Jamaica", "Japan", "Jordan",
  "Kazakhstan", "Kenya", "Kiribati", "Kosovo", "Kuwait", "Kyrgyzstan", "Laos",
  "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania",
  "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta",
  "Mauritania", "Mauritius", "Mexico", "Moldova", "Monaco", "Mongolia", "Montenegro",
  "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands",
  "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia",
  "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea",
  "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania",
  "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines",
  "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia",
  "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands",
  "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka",
  "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan",
  "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago",
  "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine",
  "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
  "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe",
];

// Inline SVG icons — rendered directly, no dependency on the theme's icon font
const IconUser = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IconMail = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 6-10 7L2 6" />
  </svg>
);
const IconLock = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const IconEye = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const IconEyeOff = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
    <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c6.5 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68M6.61 6.61A13.52 13.52 0 0 0 2 12s3.5 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
    <path d="m2 2 20 20" />
  </svg>
);
const IconArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);
const IconAlert = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v5M12 16h.01" />
  </svg>
);
const IconBuilding = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="1" />
    <path d="M9 22v-4h6v4M8 6h.01M12 6h.01M16 6h.01M8 10h.01M12 10h.01M16 10h.01M8 14h.01M12 14h.01M16 14h.01" />
  </svg>
);
const IconMapPin = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);
const IconPhone = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z" />
  </svg>
);
const IconGlobe = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20M12 2a15 15 0 0 1 0 20a15 15 0 0 1 0-20Z" />
  </svg>
);
const IconChevronDown = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m6 9 6 6 6-6" />
  </svg>
);
const IconSearch = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);
const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

// Searchable, elegant country select — no external dependency
function CountrySelect({
  value,
  onChange,
  onBlur,
  hasError,
  disabled,
}: {
  value: string;
  onChange: (val: string) => void;
  onBlur: () => void;
  hasError: boolean;
  disabled: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        if (open) {
          setOpen(false);
          setSearch("");
          onBlur();
        }
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, onBlur]);

  const filtered = COUNTRIES.filter((c) =>
    c.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="country-select" ref={containerRef}>
      <button
        type="button"
        className={`corp-input country-select-trigger ${hasError ? "has-error" : ""}`}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
      >
        <span className={value ? "" : "country-placeholder"}>
          {value || "Select your country"}
        </span>
      </button>
      <span className={`country-chevron ${open ? "open" : ""}`}>
        <IconChevronDown />
      </span>

      {open && (
        <div className="country-dropdown">
          <div className="country-search-wrapper">
            <IconSearch />
            <input
              type="text"
              autoFocus
              className="country-search-input"
              placeholder="Search countries..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <ul className="country-list">
            {filtered.length === 0 && (
              <li className="country-empty">No countries found.</li>
            )}
            {filtered.map((c) => (
              <li
                key={c}
                className={`country-option ${c === value ? "selected" : ""}`}
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                  setSearch("");
                }}
              >
                <span>{c}</span>
                {c === value && <IconCheck />}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function RegisterCustomer() {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const validateField = (
    field: keyof FormState,
    values: FormState
  ): string | undefined => {
    // Defensive fallback: guards against a stale/partial state shape
    // (e.g. leftover state from hot-reload) so the UI never crashes.
    const v = { ...INITIAL_FORM, ...values };

    switch (field) {
      case "company_name":
        if (v.type === "company" && !v.company_name.trim()) {
          return "Company name is required.";
        }
        return undefined;
      case "firstname":
        return v.firstname.trim() ? undefined : "First name is required.";
      case "lastname":
        return v.lastname.trim() ? undefined : "Last name is required.";
      case "email":
        if (!v.email.trim()) return "Email address is required.";
        if (!EMAIL_RULE.test(v.email)) return "Enter a valid email address.";
        return undefined;
      case "phone":
        // Phone is optional, but if provided it must look like a real number
        if (!v.phone.trim()) return undefined;
        if (!PHONE_RULE.test(v.phone.replace(/\s/g, ""))) {
          return "Enter a valid phone number, e.g. +968 9123 4567.";
        }
        return undefined;
      case "address":
        // Address is optional
        return undefined;
      case "country":
        return v.country.trim() ? undefined : "Please select a country.";
      case "password":
        if (!v.password) return "Password is required.";
        if (!PASSWORD_RULE.test(v.password)) {
          return "Password must be at least 8 characters, with an uppercase letter, a lowercase letter, and a number.";
        }
        return undefined;
      case "confirmPassword":
        if (!v.confirmPassword) return "Please confirm your password.";
        if (v.confirmPassword !== v.password) return "Passwords do not match.";
        return undefined;
      default:
        return undefined;
    }
  };

  const validate = (values: FormState = form): Record<string, string> => {
    const fields: (keyof FormState)[] = [
      "company_name",
      "firstname",
      "lastname",
      "email",
      "phone",
      "address",
      "country",
      "password",
      "confirmPassword",
    ];
    const next: Record<string, string> = {};
    fields.forEach((field) => {
      const err = validateField(field, values);
      if (err) next[field] = err;
    });
    return next;
  };

  const handleChange = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    setForm((prev) => {
      const updated = { ...prev, [field]: value };

      // Live-check password confirmation as the user types either field
      if (field === "password" || field === "confirmPassword") {
        setErrors((prevErrors) => {
          const nextErrors = { ...prevErrors };
          if (!updated.confirmPassword) {
            delete nextErrors.confirmPassword;
          } else if (updated.confirmPassword !== updated.password) {
            nextErrors.confirmPassword = "Passwords do not match.";
          } else {
            delete nextErrors.confirmPassword;
          }
          return nextErrors;
        });
      }

      return updated;
    });

    // Clear this field's own error as soon as the user edits it
    if (errors[field] && field !== "confirmPassword") {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleBlur = (field: keyof FormState) => () => {
    setErrors((prev) => {
      const next = { ...prev };
      const err = validateField(field, form);
      if (err) {
        next[field] = err;
      } else {
        delete next[field];
      }
      return next;
    });
  };

  const handleTypeChange = (type: AccountType) => {
    setForm((prev) => ({ ...prev, type }));
    if (type === "individual" && errors.company_name) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.company_name;
        return next;
      });
    }
  };

  const handleCountryChange = (value: string) => {
    setForm((prev) => ({ ...prev, country: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.country;
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/register-client`, {
        type: form.type,
        company_name: form.type === "company" ? form.company_name : "",
        firstname: form.firstname,
        lastname: form.lastname,
        email: form.email,
        phone: form.phone,
        address: form.address,
        country: form.country,
        password: form.password,
        password_confirmation: form.confirmPassword,
      });

      console.log(response.data);
      navigate("/login");
    } catch (error: any) {
      if (error.response && error.response.data) {
        // Laravel validation errors come back as { field: [messages] }
        const serverErrors = error.response.data.errors ?? error.response.data;
        const flattened: Record<string, string> = {};
        Object.keys(serverErrors).forEach((key) => {
          const value = serverErrors[key];
          flattened[key] = Array.isArray(value) ? value[0] : String(value);
        });
        setErrors(flattened);
        setSubmitError(
          error.response.data.message ||
            "Please check the highlighted fields and try again."
        );
      } else {
        setSubmitError("Something went wrong. Please try again.");
        console.error(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Sans:wght@300;400;500;600&display=swap');

        :root {
          --p: #294b5a;
          --p-dark: #1a313d;
          --p-light: #3a6275;
          --gold: #d3be76;
          --gold-lt: #ede4b5;
          --gold-dk: #b8a258;
          --cream: #f7f5f1;
          --muted: #7a8a91;
          --white: #ffffff;
          --error: #b9432f;
          --error-bg: #fbeae7;
        }

        .register-page {
          background: linear-gradient(135deg, var(--cream) 0%, #ffffff 100%);
          min-height: 100vh;
        }

        .corp-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: .8px;
          text-transform: uppercase;
          color: var(--gold-dk);
          margin-bottom: 10px;
        }
        .corp-eyebrow::before {
          content: '';
          display: block;
          width: 24px;
          height: 2px;
          background: var(--gold);
          border-radius: 2px;
        }

        .corp-section-title {
          font-family: 'Cormorant Garamond', Georgia, serif;
          font-size: 30px;
          font-weight: 600;
          color: var(--p-dark);
          line-height: 1.15;
          margin: 0 0 8px;
        }
        .corp-section-title em {
          color: var(--p-light);
          font-style: italic;
        }

        .corp-subtitle {
          font-size: 13px;
          color: var(--muted);
          margin: 0;
        }

        .register-card {
          background: var(--white);
          border-radius: 16px;
          box-shadow: 0 25px 50px -12px rgba(41, 75, 90, 0.25);
          overflow: hidden;
          border: 1px solid rgba(41, 75, 90, 0.08);
        }

        .register-header {
          padding: 40px 40px 22px;
          text-align: center;
          border-bottom: 1px solid rgba(41, 75, 90, 0.06);
        }

        .logo-wrapper {
          display: inline-flex;
          padding: 12px 24px;
          background: var(--cream);
          border-radius: 100px;
          margin-bottom: 18px;
          border: 1px solid rgba(211, 190, 118, 0.2);
        }

        .register-form {
          padding: 30px 40px 40px;
        }

        .form-section-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: .6px;
          text-transform: uppercase;
          color: var(--gold-dk);
          margin: 24px 0 12px;
        }
        .form-section-label:first-of-type {
          margin-top: 4px;
        }

        .corp-input-group {
          margin-bottom: 20px;
        }

        .corp-input-label {
          display: block;
          font-size: 12px;
          font-weight: 600;
          color: var(--p);
          margin-bottom: 8px;
        }

        .corp-input-wrapper {
          position: relative;
        }

        .corp-input-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          color: var(--p);
          opacity: 0.85;
          z-index: 1;
          pointer-events: none;
        }

        .password-toggle {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          padding: 4px;
          cursor: pointer;
          color: var(--p);
          opacity: 0.7;
          display: flex;
          z-index: 1;
        }
        .password-toggle:hover {
          opacity: 1;
        }

        .corp-input {
          width: 100%;
          height: 54px;
          padding: 0 16px 0 48px !important;
          border: 3px solid #94a3b8 !important;
          border-radius: 12px !important;
          font-size: 15px;
          font-family: 'DM Sans', sans-serif;
          color: var(--p-dark);
          background: var(--white) !important;
          transition: all 0.2s ease;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
          box-sizing: border-box;
        }
        .corp-input:hover {
          border-color: #6b7f88 !important;
          background-color: #fafafa !important;
        }
        .corp-input:focus {
          outline: none !important;
          border-color: var(--gold) !important;
          border-width: 3px !important;
          box-shadow: 0 0 0 4px rgba(211, 190, 118, 0.25) !important;
          background-color: #ffffff !important;
        }
        .corp-input::placeholder {
          color: #64748b;
          font-weight: 400;
          opacity: 0.8;
        }
        .corp-input.has-error {
          border-color: var(--error) !important;
          background: var(--error-bg) !important;
        }
        .corp-input:disabled {
          background: #f1f3f4 !important;
          color: #94a3b8;
          cursor: not-allowed;
        }

        .field-error {
          margin: 6px 0 0;
          font-size: 12px;
          color: var(--error);
        }

        .form-alert {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          background: var(--error-bg);
          border: 1px solid rgba(185, 67, 47, 0.25);
          color: var(--error);
          font-size: 13px;
          padding: 12px 16px;
          border-radius: 10px;
          margin-bottom: 20px;
        }
        .form-alert svg {
          flex-shrink: 0;
          margin-top: 2px;
        }

        .name-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
        }

        .password-hint {
          font-size: 11px;
          color: var(--muted);
          margin: 6px 0 0;
        }

        /* ─── ACCOUNT TYPE TOGGLE ─── */
        .type-toggle {
          display: flex;
          gap: 8px;
          background: var(--cream);
          padding: 4px;
          border-radius: 12px;
          border: 2px solid #e2e6e9;
        }
        .type-toggle-btn {
          flex: 1;
          height: 44px;
          border: none;
          background: transparent;
          border-radius: 9px;
          font-size: 13px;
          font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          color: var(--p-light);
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }
        .type-toggle-btn.active {
          background: var(--p);
          color: var(--white);
          box-shadow: 0 4px 10px rgba(41, 75, 90, 0.25);
        }
        .type-toggle-btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        /* ─── COUNTRY SELECT ─── */
        .country-select {
          position: static;
        }
        .country-select-trigger {
          display: flex;
          align-items: center;
          justify-content: flex-start;
          text-align: left;
          cursor: pointer;
          padding-right: 44px !important;
        }
        .country-select-trigger:disabled {
          cursor: not-allowed;
        }
        .country-placeholder {
          color: #9aa7ac;
        }
        .country-chevron {
          position: absolute;
          right: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--p);
          opacity: 0.7;
          pointer-events: none;
          display: flex;
          z-index: 1;
          transition: transform 0.2s ease;
        }
        .country-chevron.open {
          transform: translateY(-50%) rotate(180deg);
        }
        .country-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          right: 0;
          background: var(--white);
          border: 2px solid var(--gold);
          border-radius: 12px;
          box-shadow: 0 12px 30px -8px rgba(41, 75, 90, 0.3);
          z-index: 30;
          overflow: hidden;
        }
        .country-search-wrapper {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          border-bottom: 1px solid #e6e9eb;
        }
        .country-search-wrapper svg {
          color: var(--p);
          opacity: 0.7;
          flex-shrink: 0;
        }
        .country-search-input {
          border: none;
          outline: none;
          font-size: 14px;
          width: 100%;
          color: var(--p-dark);
          font-family: 'DM Sans', sans-serif;
        }
        .country-search-input::placeholder {
          color: #9aa7ac;
        }
        .country-list {
          list-style: none;
          margin: 0;
          padding: 6px;
          max-height: 220px;
          overflow-y: auto;
        }
        .country-option {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 12px;
          border-radius: 8px;
          font-size: 14px;
          color: var(--p-dark);
          cursor: pointer;
        }
        .country-option:hover {
          background: var(--cream);
        }
        .country-option.selected {
          background: rgba(211, 190, 118, 0.15);
          font-weight: 600;
        }
        .country-option svg {
          color: var(--gold-dk);
        }
        .country-empty {
          padding: 14px 12px;
          font-size: 13px;
          color: var(--muted);
          text-align: center;
        }

        .corp-btn-primary {
          width: 100%;
          height: 54px;
          background: var(--p);
          color: var(--white);
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }
        .corp-btn-primary:hover:not(:disabled) {
          background: var(--p-dark);
          transform: translateY(-2px);
          box-shadow: 0 10px 25px -5px rgba(41, 75, 90, 0.4);
        }
        .corp-btn-primary:active:not(:disabled) {
          transform: translateY(0);
        }
        .corp-btn-primary:disabled {
          background: #9fb0b8;
          cursor: not-allowed;
        }

        .spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.4);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .login-prompt {
          text-align: center;
          margin-top: 25px;
          padding-top: 25px;
          border-top: 2px solid #cbd5e1;
        }
        .login-prompt p {
          font-size: 14px;
          color: var(--p-light);
        }
        .login-link {
          color: var(--gold-dk);
          text-decoration: none;
          font-weight: 600;
          transition: color 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          background: rgba(211, 190, 118, 0.1);
          padding: 6px 12px;
          border-radius: 20px;
        }
        .login-link:hover {
          color: var(--p);
          background: rgba(41, 75, 90, 0.1);
        }

        .feature-list {
          margin-top: 30px;
          padding: 20px;
          background: var(--cream);
          border-radius: 12px;
          border: 2px solid #cbd5e1;
        }
        .feature-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 12px;
          color: var(--p-light);
          margin-bottom: 12px;
        }
        .feature-item:last-child {
          margin-bottom: 0;
        }
        .feature-icon {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: var(--gold);
          color: var(--p-dark);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: bold;
          border: 1px solid var(--gold-dk);
        }

        @media (max-width: 768px) {
          .register-header {
            padding: 30px 20px 15px;
          }
          .register-form {
            padding: 20px 20px 30px;
          }
          .name-row {
            grid-template-columns: 1fr;
            gap: 0;
          }
        }
      `}</style>

      <div className="register-page">
        <Header />

        <section className="layout-pt-lg layout-pb-lg" style={{ background: 'linear-gradient(135deg, var(--cream) 0%, #ffffff 100%)' }}>
          <div className="container">
            <div className="row justify-center">
              <div className="col-xl-6 col-lg-7 col-md-9">
                <div className="register-card">
                  {/* Header with Logo */}
                  <div className="register-header">
                    <div className="logo-wrapper">
                      <img
                        src="/img/logo-100.png"
                        alt="Logo"
                        style={{ height: '40px', width: 'auto' }}
                      />
                    </div>
                    <span className="corp-eyebrow">Get Started</span>
                    <h2 className="corp-section-title">
                      Create <em>Your Account</em>
                    </h2>
                    <p className="corp-subtitle">
                      Book your next trip across Oman in just a few clicks.
                    </p>
                  </div>

                  {/* Register Form */}
                  <div className="register-form">
                    <p style={{ color: 'var(--p-light)', fontSize: '14px', fontWeight: 600, marginBottom: '20px' }}>
                      Already have an account?{" "}
                      <a href="/login" className="login-link">
                        Log in
                        <IconArrowRight />
                      </a>
                    </p>

                    {submitError && (
                      <div className="form-alert">
                        <IconAlert />
                        <span>{submitError}</span>
                      </div>
                    )}

                    <form className="row y-gap-4" onSubmit={handleSubmit} noValidate>
                      {/* Account Type */}
                      <div className="col-12">
                        <p className="form-section-label">Account type</p>
                        <div className="corp-input-group">
                          <div className="type-toggle">
                            <button
                              type="button"
                              className={`type-toggle-btn ${form.type === "individual" ? "active" : ""}`}
                              onClick={() => handleTypeChange("individual")}
                              disabled={isSubmitting}
                            >
                              <IconUser />
                              Individual
                            </button>
                            <button
                              type="button"
                              className={`type-toggle-btn ${form.type === "company" ? "active" : ""}`}
                              onClick={() => handleTypeChange("company")}
                              disabled={isSubmitting}
                            >
                              <IconBuilding />
                              Company
                            </button>
                          </div>
                        </div>

                        {form.type === "company" && (
                          <div className="corp-input-group">
                            <label className="corp-input-label">Company Name</label>
                            <div className="corp-input-wrapper">
                              <span className="corp-input-icon"><IconBuilding /></span>
                              <input
                                type="text"
                                className={`corp-input ${errors.company_name ? "has-error" : ""}`}
                                placeholder="Muscat Trading LLC"
                                value={form.company_name}
                                onChange={handleChange("company_name")}
                                onBlur={handleBlur("company_name")}
                                disabled={isSubmitting}
                              />
                            </div>
                            {errors.company_name && <p className="field-error">{errors.company_name}</p>}
                          </div>
                        )}
                      </div>

                      {/* Name Fields Row */}
                      <div className="col-12">
                        <p className="form-section-label">Personal details</p>
                        <div className="name-row">
                          {/* First Name */}
                          <div className="corp-input-group">
                            <label className="corp-input-label">First Name</label>
                            <div className="corp-input-wrapper">
                              <span className="corp-input-icon"><IconUser /></span>
                              <input
                                type="text"
                                className={`corp-input ${errors.firstname ? "has-error" : ""}`}
                                placeholder="Ahmed"
                                value={form.firstname}
                                onChange={handleChange("firstname")}
                                onBlur={handleBlur("firstname")}
                                disabled={isSubmitting}
                              />
                            </div>
                            {errors.firstname && <p className="field-error">{errors.firstname}</p>}
                          </div>

                          {/* Last Name */}
                          <div className="corp-input-group">
                            <label className="corp-input-label">Last Name</label>
                            <div className="corp-input-wrapper">
                              <span className="corp-input-icon"><IconUser /></span>
                              <input
                                type="text"
                                className={`corp-input ${errors.lastname ? "has-error" : ""}`}
                                placeholder="Al Balushi"
                                value={form.lastname}
                                onChange={handleChange("lastname")}
                                onBlur={handleBlur("lastname")}
                                disabled={isSubmitting}
                              />
                            </div>
                            {errors.lastname && <p className="field-error">{errors.lastname}</p>}
                          </div>
                        </div>

                        {/* Email */}
                        <div className="corp-input-group">
                          <label className="corp-input-label">Email Address</label>
                          <div className="corp-input-wrapper">
                            <span className="corp-input-icon"><IconMail /></span>
                            <input
                              type="email"
                              className={`corp-input ${errors.email ? "has-error" : ""}`}
                              placeholder="ahmed.albalushi@example.com"
                              value={form.email}
                              onChange={handleChange("email")}
                              onBlur={handleBlur("email")}
                              disabled={isSubmitting}
                            />
                          </div>
                          {errors.email && <p className="field-error">{errors.email}</p>}
                        </div>

                        {/* Phone */}
                        <div className="corp-input-group">
                          <label className="corp-input-label">
                            Phone Number <span style={{ color: 'var(--muted)', fontWeight: 400, textTransform: 'none' }}>(optional)</span>
                          </label>
                          <div className="corp-input-wrapper">
                            <span className="corp-input-icon"><IconPhone /></span>
                            <input
                              type="tel"
                              className={`corp-input ${errors.phone ? "has-error" : ""}`}
                              placeholder="+968 9123 4567"
                              value={form.phone}
                              onChange={handleChange("phone")}
                              onBlur={handleBlur("phone")}
                              disabled={isSubmitting}
                            />
                          </div>
                          {errors.phone && <p className="field-error">{errors.phone}</p>}
                        </div>

                        {/* Address */}
                        <div className="corp-input-group">
                          <label className="corp-input-label">
                            Address <span style={{ color: 'var(--muted)', fontWeight: 400, textTransform: 'none' }}>(optional)</span>
                          </label>
                          <div className="corp-input-wrapper">
                            <span className="corp-input-icon"><IconMapPin /></span>
                            <input
                              type="text"
                              className={`corp-input ${errors.address ? "has-error" : ""}`}
                              placeholder="Way 3012, Building 45, Al Khuwair, Muscat"
                              value={form.address}
                              onChange={handleChange("address")}
                              onBlur={handleBlur("address")}
                              disabled={isSubmitting}
                            />
                          </div>
                          {errors.address && <p className="field-error">{errors.address}</p>}
                        </div>

                        {/* Country */}
                        <div className="corp-input-group">
                          <label className="corp-input-label">Country</label>
                          <div className="corp-input-wrapper">
                            <span className="corp-input-icon"><IconGlobe /></span>
                            <CountrySelect
                              value={form.country}
                              onChange={handleCountryChange}
                              onBlur={handleBlur("country")}
                              hasError={!!errors.country}
                              disabled={isSubmitting}
                            />
                          </div>
                          {errors.country && <p className="field-error">{errors.country}</p>}
                        </div>
                      </div>

                      {/* Password */}
                      <div className="col-12">
                        <p className="form-section-label">Account security</p>
                        <div className="corp-input-group">
                          <label className="corp-input-label">Password</label>
                          <div className="corp-input-wrapper">
                            <span className="corp-input-icon"><IconLock /></span>
                            <input
                              type={showPassword ? "text" : "password"}
                              className={`corp-input ${errors.password ? "has-error" : ""}`}
                              placeholder="••••••••"
                              value={form.password}
                              onChange={handleChange("password")}
                              onBlur={handleBlur("password")}
                              disabled={isSubmitting}
                            />
                            <button
                              type="button"
                              className="password-toggle"
                              onClick={() => setShowPassword(!showPassword)}
                              tabIndex={-1}
                            >
                              {showPassword ? <IconEyeOff /> : <IconEye />}
                            </button>
                          </div>
                          {errors.password ? (
                            <p className="field-error">{errors.password}</p>
                          ) : (
                            <p className="password-hint">
                              At least 8 characters, with an uppercase letter, a lowercase letter, and a number.
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Confirm Password */}
                      <div className="col-12">
                        <div className="corp-input-group">
                          <label className="corp-input-label">Confirm Password</label>
                          <div className="corp-input-wrapper">
                            <span className="corp-input-icon"><IconLock /></span>
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              className={`corp-input ${errors.confirmPassword ? "has-error" : ""}`}
                              placeholder="••••••••"
                              value={form.confirmPassword}
                              onChange={handleChange("confirmPassword")}
                              onBlur={handleBlur("confirmPassword")}
                              disabled={isSubmitting}
                            />
                            <button
                              type="button"
                              className="password-toggle"
                              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              tabIndex={-1}
                            >
                              {showConfirmPassword ? <IconEyeOff /> : <IconEye />}
                            </button>
                          </div>
                          {errors.confirmPassword && <p className="field-error">{errors.confirmPassword}</p>}
                        </div>
                      </div>

                      {/* Submit Button */}
                      <div className="col-12">
                        <button
                          type="submit"
                          className="corp-btn-primary"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner" />
                              <span>Creating your account...</span>
                            </>
                          ) : (
                            <>
                              <span>Create Account</span>
                              <IconArrowRight />
                            </>
                          )}
                        </button>
                      </div>
                    </form>

                    {/* Feature List */}
                    <div className="feature-list">
                      <div className="feature-item">
                        <span className="feature-icon">✓</span>
                        <span>Free account with full access</span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-icon">✓</span>
                        <span>Manage your bookings in one place</span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-icon">✓</span>
                        <span>Get exclusive member discounts</span>
                      </div>
                      <div className="feature-item">
                        <span className="feature-icon">✓</span>
                        <span>Secure and encrypted data</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <DefaultFooter />
        <WhatsAppButton />
      </div>
    </>
  );
}

export default RegisterCustomer;